import { Fragment, useEffect } from 'react';

import BlogCategoryPage from '@/components/BasePages/BlogCategoryPage';
import BlogPage from '@/components/BasePages/BlogPage';
import DynamicRedirectPage from '@/components/BasePages/DynamicRedirectPage/DynamicRedirectPage';
import StandardPage from '@/components/BasePages/StandardPage';
import SunrunShopProductPage from '@/components/BasePages/SunrunShopProductPage/SunrunShopProductPage';
import ConstrainedExitIntent from '@/components/ExitIntent/ConstrainedExitIntent';
import {
  type BlogCategoryPageContentType,
  type BlogPageContent,
  type PageContent,
  PageContentType,
  type StandardPageContent,
} from '@/contentful';
import type { SunrunShopProductPageEnhancedCF } from '@/contentful/api/api.types';
import useEcommMarket from '@/hooks/geo';
import type { DynamicRedirectPageCF } from '@/types';

interface Props {
  content: PageContent;
}

interface IWindow extends Window {
  FS: (arg: string) => void;
}

export default function Page({
  content: {
    fields: { fullStoryEnabled, pageContent, seoFields, path, exitIntent },
  },
}: Props): React.JSX.Element | null {
  const { userLocation, setUserLocation } = useEcommMarket();

  // biome-ignore lint/correctness/useExhaustiveDependencies: Automated migration to Biome
  useEffect(() => {
    const interval = setInterval(() => {
      if (fullStoryEnabled) {
        const isReady =
          typeof (window as unknown as IWindow)?.FS === 'function';

        if (isReady) {
          (window as unknown as IWindow).FS('start');
          clearInterval(interval);
        }
      }
    }, 5 * 1000);

    return () => clearInterval(interval);
  }, []);

  switch (pageContent?.sys?.contentType.sys.id) {
    case PageContentType.STANDARD:
      return (
        <Fragment>
          <StandardPage
            content={pageContent as StandardPageContent}
            userLocation={userLocation}
            setUserLocation={setUserLocation}
          />
          {exitIntent && (
            <ConstrainedExitIntent
              exitIntentConfig={exitIntent}
              userLocation={userLocation}
            />
          )}
        </Fragment>
      );
    case PageContentType.BLOG:
      return (
        <Fragment>
          <BlogPage
            path={path}
            seoFields={seoFields}
            content={pageContent as BlogPageContent}
            userLocation={userLocation}
            setUserLocation={setUserLocation}
          />
          {exitIntent && (
            <ConstrainedExitIntent
              exitIntentConfig={exitIntent}
              userLocation={userLocation}
            />
          )}
        </Fragment>
      );
    case PageContentType.BLOG_CATEGORY:
      return (
        <Fragment>
          <BlogCategoryPage
            content={pageContent as BlogCategoryPageContentType}
            userLocation={userLocation}
            setUserLocation={setUserLocation}
          />
          {exitIntent && (
            <ConstrainedExitIntent
              exitIntentConfig={exitIntent}
              userLocation={userLocation}
            />
          )}
        </Fragment>
      );
    case PageContentType.DYNAMIC_REDIRECT:
      return (
        <DynamicRedirectPage content={pageContent as DynamicRedirectPageCF} />
      );
    case PageContentType.SUNRUN_SHOP_PRODUCT:
      return (
        <SunrunShopProductPage
          content={pageContent as SunrunShopProductPageEnhancedCF}
          userLocation={userLocation}
          setUserLocation={setUserLocation}
        />
      );
    default:
      return null;
  }
}
